import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';

import { MAX_SORT_COLUMNS } from '@/shared/lib/sorting';

import { DropdownMenuItem } from './GeneratikTableToolbar.styles';
import { SortOption } from './GeneratikTableToolbar.types';
import {
  sortDirectionIconSx,
  sortLevelBadgeSx,
  sortLevelIconButtonSx,
  sortLevelLabelSx,
  sortLevelRowSx,
  sortSectionLabelSx,
} from './ToolbarSortMenu.styles';
import { UseSortModelActionsReturn } from './hooks/useSortModelActions';

export interface ToolbarSortMenuProps {
  options: SortOption[];
  sortActions: UseSortModelActionsReturn;
  campaignColumnVisibility?: Record<string, boolean>;
  onDisabledItemClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onClose: () => void;
}

const ToolbarSortMenu = ({
  options,
  sortActions,
  campaignColumnVisibility,
  onDisabledItemClick,
  onClose,
}: ToolbarSortMenuProps) => {
  const { t } = useTranslation();
  const { sortModel, setDirection, remove, move, clear, canAddLevel } = sortActions;

  const labelByField = useMemo(
    () => new Map(options.map((option) => [option.field, option.label])),
    [options],
  );

  const availableOptions = useMemo(
    () =>
      options
        .filter((option) => !sortModel.some((item) => item.field === option.field))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [options, sortModel],
  );

  const showPriority = sortModel.length > 1;

  return (
    <Box data-testid="sort-menu">
      {sortModel.length > 0 && (
        <>
          <Typography sx={sortSectionLabelSx}>{t('sort_levels')}</Typography>
          {sortModel.map((item, index) => {
            const isAscending = item.sort !== 'desc';
            return (
              <Box key={item.field} sx={sortLevelRowSx} data-testid={`sort-level-${item.field}`}>
                {showPriority && (
                  <Box
                    component="span"
                    sx={sortLevelBadgeSx}
                    data-testid={`sort-level-priority-${item.field}`}
                  >
                    {index + 1}
                  </Box>
                )}
                <Typography sx={sortLevelLabelSx} title={labelByField.get(item.field)}>
                  {labelByField.get(item.field) ?? item.field}
                </Typography>
                <Tooltip title={isAscending ? t('sort_descending') : t('sort_ascending')}>
                  <IconButton
                    size="small"
                    sx={sortLevelIconButtonSx}
                    onClick={() => setDirection(item.field, isAscending ? 'desc' : 'asc')}
                    data-testid={`sort-direction-${item.field}`}
                  >
                    {isAscending ? (
                      <ArrowUpwardIcon sx={sortDirectionIconSx} />
                    ) : (
                      <ArrowDownwardIcon sx={sortDirectionIconSx} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('move_sort_level_up')}>
                  <span>
                    <IconButton
                      size="small"
                      sx={sortLevelIconButtonSx}
                      disabled={index === 0}
                      onClick={() => move(item.field, -1)}
                      data-testid={`sort-move-up-${item.field}`}
                    >
                      <KeyboardArrowUpIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('move_sort_level_down')}>
                  <span>
                    <IconButton
                      size="small"
                      sx={sortLevelIconButtonSx}
                      disabled={index === sortModel.length - 1}
                      onClick={() => move(item.field, 1)}
                      data-testid={`sort-move-down-${item.field}`}
                    >
                      <KeyboardArrowDownIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('remove_sort_level')}>
                  <IconButton
                    size="small"
                    sx={sortLevelIconButtonSx}
                    onClick={() => remove(item.field)}
                    data-testid={`sort-remove-${item.field}`}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
          <Divider />
        </>
      )}

      {availableOptions.length > 0 && (
        <>
          <Typography sx={sortSectionLabelSx}>
            {sortModel.length > 0 ? t('add_sort_level') : t('sort_by')}
          </Typography>
          {availableOptions.map((option) => {
            const blockedByCampaign = campaignColumnVisibility?.[option.field] === false;
            const disabled = blockedByCampaign || !canAddLevel;
            return (
              <Tooltip
                key={option.field}
                title={
                  blockedByCampaign
                    ? t('Campaign period is required')
                    : !canAddLevel
                      ? t('sort_limit_reached', { count: MAX_SORT_COLUMNS })
                      : ''
                }
              >
                <span
                  data-testid={`sort-add-blocked-${option.field}`}
                  onClick={blockedByCampaign ? onDisabledItemClick : undefined}
                >
                  <DropdownMenuItem
                    disabled={disabled}
                    onClick={() => setDirection(option.field, 'asc')}
                    data-testid={`sort-add-${option.field}`}
                  >
                    <span>{option.label}</span>
                    <AddIcon sx={sortDirectionIconSx} />
                  </DropdownMenuItem>
                </span>
              </Tooltip>
            );
          })}
        </>
      )}

      {sortModel.length > 0 && (
        <>
          <Divider />
          <DropdownMenuItem
            onClick={() => {
              clear();
              onClose();
            }}
            data-testid="sort-clear"
          >
            <span>{t('clear_sorting')}</span>
          </DropdownMenuItem>
        </>
      )}
    </Box>
  );
};

export default ToolbarSortMenu;
